import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './MesDemandesPermission.css';

const MesDemandesPermission = () => {
  const [demandes, setDemandes] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    date_debut: '',
    date_fin: '',
    type: 'conge',
    raison: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadDemandes();
  }, []);

  const loadDemandes = async () => {
    try {
      setLoading(true);
      const response = await api.demandesPermission.mesDemandes();
      setDemandes(response.demandes || []);
      setStatistiques(response.statistiques || null);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
      alert('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors = {};
    if (!formData.date_debut) newErrors.date_debut = 'Date de début requise';
    if (!formData.date_fin) newErrors.date_fin = 'Date de fin requise';
    if (formData.date_fin < formData.date_debut) {
      newErrors.date_fin = 'La date de fin doit être après la date de début';
    }
    if (!formData.raison || formData.raison.length < 10) {
      newErrors.raison = 'La raison doit contenir au moins 10 caractères';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await api.demandesPermission.create(formData);
      alert('✅ Demande envoyée avec succès !');
      setShowModal(false);
      setFormData({ date_debut: '', date_fin: '', type: 'conge', raison: '' });
      loadDemandes();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('❌ ' + (error.message || 'Erreur lors de la création de la demande'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      await api.demandesPermission.delete(id);
      alert('✅ Demande supprimée');
      loadDemandes();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('❌ ' + (error.message || 'Erreur lors de la suppression'));
    }
  };

  const getBadgeStyle = (statut) => {
    switch(statut) {
      case 'en_attente':
        return { bg: '#ff9800', text: '⏳ En attente' };
      case 'approuvee':
        return { bg: '#4caf50', text: '✅ Approuvée' };
      case 'refusee':
        return { bg: '#ef5350', text: '❌ Refusée' };
      default:
        return { bg: '#778', text: statut };
    }
  };

  const getTypeBadge = (type) => {
    switch(type) {
      case 'conge':
        return { bg: '#42a5f5', icon: '🏖️', text: 'Congé' };
      case 'absence':
        return { bg: '#ffc107', icon: '🚫', text: 'Absence' };
      case 'maladie':
        return { bg: '#ef5350', icon: '🤒', text: 'Maladie' };
      case 'autre':
        return { bg: '#778899', icon: '📋', text: 'Autre' };
      default:
        return { bg: '#778', icon: '📋', text: type };
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const calculateDays = () => {
    if (formData.date_debut && formData.date_fin) {
      const debut = new Date(formData.date_debut);
      const fin = new Date(formData.date_fin);
      const diffTime = Math.abs(fin - debut);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="demandes-permission-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="demandes-permission-container">
      {/* Header */}
      <div className="demandes-header">
        <div>
          <h2 className="demandes-title">📝 Mes Demandes de Permission</h2>
          <p className="demandes-subtitle">
            Gérez vos demandes de congés, absences et permissions
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          ➕ Nouvelle demande
        </button>
      </div>

      {/* Statistiques */}
      {statistiques && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-value">{statistiques.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-icon">⏳</div>
            <div className="stat-value">{statistiques.en_attente}</div>
            <div className="stat-label">En attente</div>
          </div>
          <div className="stat-card green">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{statistiques.approuvees}</div>
            <div className="stat-label">Approuvées</div>
          </div>
          <div className="stat-card red">
            <div className="stat-icon">❌</div>
            <div className="stat-value">{statistiques.refusees}</div>
            <div className="stat-label">Refusées</div>
          </div>
        </div>
      )}

      {/* Liste des demandes */}
      <div className="demandes-list-card">
        {demandes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <p>Aucune demande de permission</p>
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              Créer ma première demande
            </button>
          </div>
        ) : (
          <div className="demandes-table-wrapper">
            <table className="demandes-table">
              <thead>
                <tr>
                  <th>Période</th>
                  <th>Type</th>
                  <th>Jours</th>
                  <th>Raison</th>
                  <th>Statut</th>
                  <th>Date demande</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {demandes.map((demande) => {
                  const statusBadge = getBadgeStyle(demande.statut);
                  const typeBadge = getTypeBadge(demande.type);
                  
                  return (
                    <tr key={demande.id}>
                      <td>
                        <div className="periode-cell">
                          <strong>{formatDate(demande.date_debut)}</strong>
                          <span className="date-separator">→</span>
                          <strong>{formatDate(demande.date_fin)}</strong>
                        </div>
                      </td>
                      <td>
                        <span 
                          className="badge-type" 
                          style={{ backgroundColor: typeBadge.bg }}
                        >
                          {typeBadge.icon} {typeBadge.text}
                        </span>
                      </td>
                      <td className="text-center">
                        <strong>{demande.nombre_jours}</strong> jour{demande.nombre_jours > 1 ? 's' : ''}
                      </td>
                      <td>
                        <div className="raison-cell" title={demande.raison}>
                          {demande.raison.substring(0, 50)}
                          {demande.raison.length > 50 ? '...' : ''}
                        </div>
                        {demande.commentaire_dg && (
                          <div className="commentaire-dg">
                            <strong>Commentaire DG:</strong> {demande.commentaire_dg}
                          </div>
                        )}
                      </td>
                      <td>
                        <span 
                          className="badge-statut" 
                          style={{ backgroundColor: statusBadge.bg }}
                        >
                          {statusBadge.text}
                        </span>
                      </td>
                      <td className="text-muted">
                        {formatDate(demande.created_at)}
                      </td>
                      <td>
                        {demande.statut === 'en_attente' && (
                          <button 
                            className="btn-delete"
                            onClick={() => handleDelete(demande.id)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Nouvelle demande de permission</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label>Type de demande *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="form-control"
                >
                  <option value="conge">🏖️ Congé</option>
                  <option value="absence">🚫 Absence</option>
                  <option value="maladie">🤒 Maladie</option>
                  <option value="autre">📋 Autre</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de début *</label>
                  <input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                    className={`form-control ${errors.date_debut ? 'error' : ''}`}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  {errors.date_debut && <span className="error-text">{errors.date_debut}</span>}
                </div>

                <div className="form-group">
                  <label>Date de fin *</label>
                  <input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                    className={`form-control ${errors.date_fin ? 'error' : ''}`}
                    min={formData.date_debut || new Date().toISOString().split('T')[0]}
                  />
                  {errors.date_fin && <span className="error-text">{errors.date_fin}</span>}
                </div>
              </div>

              {formData.date_debut && formData.date_fin && (
                <div className="info-box">
                  📅 Durée: <strong>{calculateDays()} jour{calculateDays() > 1 ? 's' : ''}</strong>
                </div>
              )}

              <div className="form-group">
                <label>Raison de la demande *</label>
                <textarea
                  value={formData.raison}
                  onChange={(e) => setFormData({ ...formData, raison: e.target.value })}
                  className={`form-control ${errors.raison ? 'error' : ''}`}
                  rows="4"
                  placeholder="Expliquez la raison de votre demande (minimum 10 caractères)..."
                  minLength="10"
                  maxLength="1000"
                />
                <div className="char-count">
                  {formData.raison.length}/1000 caractères
                </div>
                {errors.raison && <span className="error-text">{errors.raison}</span>}
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  📤 Envoyer la demande
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MesDemandesPermission;
